import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Resource } from '../types';

const DownloadableResources: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const resourceTypes = [
    { id: 'all', name: 'All Resources', icon: 'Grid' },
    { id: 'guide', name: 'Implementation Guides', icon: 'BookOpen' },
    { id: 'template', name: 'Templates', icon: 'FileText' },
    { id: 'checklist', name: 'Checklists', icon: 'CheckSquare' },
    { id: 'documentation', name: 'Documentation', icon: 'File' }
  ];

  const resources: Resource[] = [
    {
      id: '1',
      title: 'Complete Implementation Checklist',
      description: 'Comprehensive step-by-step checklist covering all aspects of camlytix AI deployment from planning to go-live.',
      category: 'getting-started',
      type: 'checklist',
      difficulty: 'beginner',
      tags: ['deployment', 'planning', 'checklist'],
      downloadUrl: '/downloads/implementation-checklist.pdf',
      lastUpdated: '2024-01-15'
    },
    {
      id: '2',
      title: 'API Integration Template',
      description: 'Ready-to-use code templates for integrating camlytix AI APIs with your existing systems and applications.',
      category: 'integration',
      type: 'template',
      difficulty: 'intermediate',
      tags: ['api', 'integration', 'code'],
      downloadUrl: '/downloads/api-integration-template.zip',
      lastUpdated: '2024-01-14'
    },
    {
      id: '3',
      title: 'Camera Configuration Best Practices Guide',
      description: 'Expert recommendations for optimal camera placement, configuration, and calibration to maximize analytics accuracy.',
      category: 'best-practices',
      type: 'guide',
      difficulty: 'intermediate',
      tags: ['cameras', 'configuration', 'accuracy'],
      downloadUrl: '/downloads/camera-configuration-guide.pdf',
      lastUpdated: '2024-01-13'
    },
    {
      id: '4',
      title: 'System Architecture Documentation',
      description: 'Detailed technical documentation covering camlytix AI architecture, components, and infrastructure requirements.',
      category: 'api',
      type: 'documentation',
      difficulty: 'advanced',
      tags: ['architecture', 'technical', 'infrastructure'],
      downloadUrl: '/downloads/system-architecture.pdf',
      lastUpdated: '2024-01-12'
    },
    {
      id: '5',
      title: 'ROI Calculation Spreadsheet',
      description: 'Excel template for calculating expected return on investment from camlytix AI deployment with customizable parameters.',
      category: 'getting-started',
      type: 'template',
      difficulty: 'beginner',
      tags: ['roi', 'business-case', 'planning'],
      downloadUrl: '/downloads/roi-calculator.xlsx',
      lastUpdated: '2024-01-11'
    },
    {
      id: '6',
      title: 'Troubleshooting Quick Reference',
      description: 'Quick reference guide for diagnosing and resolving common implementation issues and error messages.',
      category: 'troubleshooting',
      type: 'guide',
      difficulty: 'intermediate',
      tags: ['troubleshooting', 'errors', 'support'],
      downloadUrl: '/downloads/troubleshooting-guide.pdf',
      lastUpdated: '2024-01-10'
    },
    {
      id: '7',
      title: 'Security Configuration Checklist',
      description: 'Comprehensive security checklist ensuring your camlytix AI deployment meets industry standards and compliance requirements.',
      category: 'best-practices',
      type: 'checklist',
      difficulty: 'advanced',
      tags: ['security', 'compliance', 'gdpr'],
      downloadUrl: '/downloads/security-checklist.pdf',
      lastUpdated: '2024-01-09'
    },
    {
      id: '8',
      title: 'Dashboard Widget Templates',
      description: 'Pre-built dashboard widget templates for common analytics use cases including retail, traffic, and security monitoring.',
      category: 'integration',
      type: 'template',
      difficulty: 'intermediate',
      tags: ['dashboard', 'widgets', 'visualization'],
      downloadUrl: '/downloads/dashboard-templates.zip',
      lastUpdated: '2024-01-08'
    }
  ];

  const filteredResources = activeFilter === 'all' 
    ? resources 
    : resources.filter(resource => resource.type === activeFilter);

  const getDifficultyColor = (difficulty: Resource['difficulty']) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-success/10 text-success border-success/30';
      case 'intermediate':
        return 'bg-warning/10 text-warning border-warning/30';
      case 'advanced':
        return 'bg-error/10 text-error border-error/30';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const getTypeIcon = (type: Resource['type']) => {
    switch (type) {
      case 'guide':
        return 'BookOpen';
      case 'template':
        return 'FileText';
      case 'checklist':
        return 'CheckSquare';
      case 'documentation':
        return 'File';
      default:
        return 'FileText';
    }
  };

  return (
    <section className="py-16 px-4 md:px-6 lg:px-8 bg-surface">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Downloadable Resources
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Access our library of implementation guides, templates, checklists, and documentation to accelerate your deployment and ensure success.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {resourceTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveFilter(type.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                activeFilter === type.id
                  ? 'bg-primary text-primary-foreground shadow-brand'
                  : 'bg-card text-text-secondary hover:bg-muted border border-border'
              }`}
            >
              <Icon name={type.icon as any} size={18} />
              <span>{type.name}</span>
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => (
            <div
              key={resource.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-brand-lg transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name={getTypeIcon(resource.type) as any} size={24} className="text-primary" />
                </div>
                <div className={`px-2 py-1 rounded-full border text-xs font-medium ${getDifficultyColor(resource.difficulty)}`}>
                  {resource.difficulty}
                </div>
              </div>

              <h3 className="text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                {resource.title}
              </h3>

              <p className="text-sm text-text-secondary mb-4 line-clamp-3">
                {resource.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {resource.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-muted rounded-md text-xs text-text-secondary"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <span className="text-xs text-text-secondary">
                  Updated {new Date(resource.lastUpdated).toLocaleDateString()}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => console.log(`Downloading: ${resource.title}`)}
                >
                  Download
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredResources.length === 0 && (
          <div className="text-center py-12">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No resources found</h3>
            <p className="text-text-secondary mb-6">
              Try selecting a different resource type
            </p>
            <Button
              variant="outline"
              size="default"
              onClick={() => setActiveFilter('all')}
            >
              Show All Resources
            </Button>
          </div>
        )}

        <div className="mt-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl border border-primary/20 p-8 text-center">
          <Icon name="Package" size={48} className="text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Need Custom Resources?
          </h3>
          <p className="text-text-secondary max-w-2xl mx-auto mb-6">
            Our team can create customized implementation guides, templates, and documentation tailored to your specific use case and industry requirements.
          </p>
          <Button
            variant="default"
            size="lg"
            iconName="Mail"
            iconPosition="left"
            onClick={() => console.log('Request custom resources')}
          >
            Request Custom Resources
          </Button>
        </div>
      </div>
    </section>
  );
};

export default DownloadableResources;